import React from 'react';
import { Col, Collapse, Row, Table } from 'antd';
import PrintIcon from 'icons/print.svg';
import Cell from './Cell';
import CmsTable from 'mixins/ui/cms-table';
import PrintForm2 from 'mixins/ui/print-form-2';
import { TResults } from './upcoming-payments.type';
import dayjs from 'mixins/dayjsConfig.js';
import Button from 'mixins/ui/button/button';
import { localize } from '/react4xp/mixins/helpers';
import { m } from 'framer-motion';

const Results = ({ data, printUrl, translations, language, token, commonTranslations}: TResults) => {
    const columns = [
        {
            title: localize('upcoming-payments-as-of', translations),
            dataIndex: 'reportAccrualDate',
            render: (reportAccrualDate) =>
                reportAccrualDate && dayjs(reportAccrualDate.substring(0, 10)).format('DD.MM.YYYY'),
        },
        {
            title: localize('upcoming-payments-total_arrear_kzt', translations),
            dataIndex: 'totalArrear',
        },
        {
            title: localize('upcoming-payments-total_upcoming_payments_kzt', translations),
            dataIndex: 'totalUpcPays',
        },
    ];

    const secondColumns = [
        {
            title: localize('upcoming-payments-bcc', translations),
            dataIndex: 'bcc',
            render: (bcc, row) => bcc + ' ' + row.bccNameRu,
        },
        {
            title: localize('upcoming-payments-arrear_of_tax', translations),
            dataIndex: 'taxArrear',
            render: (_, row) => {
                return (
                    <Cell
                        bcc={row.bcc}
                        cellValue={row.taxArrear}
                        iinBin={data.iinBin}
                        msuCode={row.charCode}
                        knp={911}
                        language={language}
                        translations={commonTranslations}
                    />
                );
            },
        },
        {
            title: localize('upcoming-payments-arrear_of_poena', translations),
            dataIndex: 'poenaArrear',
            render: (_, row) => {
                return (
                    <Cell
                        bcc={row.bcc}
                        cellValue={row.poenaArrear}
                        iinBin={data.iinBin}
                        msuCode={row.charCode}
                        knp={912}
                        language={language}
                        translations={commonTranslations}
                    />
                );
            },
        },
        {
            title: localize('upcoming-payments-arrear_of_fine', translations),
            dataIndex: 'fineArrear',
            render: (_, row) => {
                return (
                    <Cell
                        bcc={row.bcc}
                        cellValue={row.fineArrear}
                        iinBin={data.iinBin}
                        msuCode={row.charCode}
                        knp={913}
                        language={language}
                        translations={commonTranslations}
                    />
                );
            },
        },
        {
            title: localize('upcoming-payments-arrear_of_percent', translations),
            dataIndex: 'percentArrear',
        },
        {
            title: localize('upcoming-payments-total_arrear', translations),
            dataIndex: 'totalArrear',
        },
    ];

    const thirdColumns = [
        {
            title: localize('upcoming-payments-bcc', translations),
            dataIndex: 'bccFull',
        },
        {
            title: localize('upcoming-payments-due-date', translations),
            dataIndex: 'applyDate',
            render: (applyDate) =>
                applyDate && dayjs(applyDate.substring(0, 10)).format('DD.MM.YYYY'),
        },
        {
            title: localize('upcoming-payments-sum', translations),
            dataIndex: 'amount',
            render: (amount, row) => {
                return (
                    <Cell
                        bcc={row.bcc}
                        cellValue={amount}
                        iinBin={data.iinBin}
                        msuCode={row.charCode}
                        knp={911}
                        language={language}
                        translations={commonTranslations}
                    />
                );
            },
        },
    ];

    const CollapseHeader = (charCode, nameRu) => {
        return (
            <>
                <Row
                    className="second-table-data mt-3"
                    gutter={16}
                >
                    <Col>
                        <div className="mb-3">{localize('upcoming-payments-gra_code', translations)}</div>
                        <div>{charCode}</div>
                    </Col>
                    <Col md={11}>
                        <div className="mb-3">{localize('upcoming-payments-gra_name', translations)}</div>
                        <div>{nameRu}</div>
                    </Col>
                </Row>
                <Col
                    md={24}
                    xs={24}
                    lg={24}
                >
                    <Button color="borderless">{localize('upcoming-payments-read-more', translations)}</Button>
                </Col>
            </>
        );
    };

    return (
        <>
            <CmsTable
                resultCard={data.nameRu}
                noResNumber
                dataSource={[data]}
                columns={columns}
                pagination={false}
                type="default"
                translations={commonTranslations}
            >
                <Col
                    md={12}
                    xs={12}
                    lg={12}
                >
                    <div>{localize('iin-bin', commonTranslations)}</div>
                    <div>{data.iinBin}</div>
                </Col>
            </CmsTable>
            {data.taxOrgInfos?.length > 0 && (
                <Row gutter={15}>
                    <Col
                        md={24}
                        xs={24}
                        lg={24}
                        className="my-3 fs-5"
                    >
                        {localize('upcoming-payments-table_arrears', translations)}
                    </Col>
                    <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
                        <Collapse
                            defaultActiveKey={['0']}
                            collapsible="header"
                        >
                            {data.taxOrgInfos.map((item, id) => {
                                const upcPayInfos = item.bccInfos?.reduce((acc, bccInfo) => {
                                    if (bccInfo.upcPayInfos && bccInfo.upcPayInfos.length > 0) {
                                        return acc.concat(bccInfo.upcPayInfos);
                                    } else {
                                        return acc;
                                    }
                                }, []);

                                return (
                                    item.bccInfos && (
                                        <>
                                            {item.bccInfos.length > 0 && (
                                                <Collapse.Panel
                                                    header={CollapseHeader(
                                                        item.charCode,
                                                        item.nameRu
                                                    )}
                                                    key={id}
                                                    showArrow={false}
                                                >
                                                    {upcPayInfos.length > 0 && (
                                                        <Row>
                                                            <Col
                                                                md={24}
                                                                xs={24}
                                                                lg={24}
                                                                className="my-3 fs-6"
                                                            >
                                                                {localize('upcoming-payments-table', translations)}
                                                            </Col>
                                                            <Table
                                                                columns={thirdColumns}
                                                                dataSource={upcPayInfos}
                                                                className="table-default"
                                                                scroll={{ y: 500 }}
                                                            />
                                                        </Row>
                                                    )}
                                                    <CmsTable
                                                        showResponseBlock={false}
                                                        noResNumber
                                                        columns={secondColumns}
                                                        dataSource={item.bccInfos}
                                                        className="table-default"
                                                        size="small"
                                                        translations={commonTranslations}
                                                    />
                                                </Collapse.Panel>
                                            )}
                                        </>
                                    )
                                );
                            })}
                        </Collapse>
                    </div>
                    <Row
                        gutter={15}
                        justify="end"
                        className="mt-3"
                    >
                        <Col>
                            <PrintForm2
                                apiUrl={printUrl}
                                tokenStr={token}
                                icon={<PrintIcon />}
                                translations={commonTranslations}
                            />
                        </Col>
                    </Row>
                </Row>
            )}
        </>
    );
};

export default Results;
